import dancingScript400 from "src/assets/fonts/dancing-script-latin-400-normal.woff2";
import dancingScript700 from "src/assets/fonts/dancing-script-latin-700-normal.woff2";
import caveat400 from "src/assets/fonts/caveat-latin-400-normal.woff2";
import caveat700 from "src/assets/fonts/caveat-latin-700-normal.woff2";
import allura400 from "src/assets/fonts/allura-latin-400-normal.woff2";
import greatVibes400 from "src/assets/fonts/great-vibes-latin-400-normal.woff2";
import homemadeApple400 from "src/assets/fonts/homemade-apple-latin-400-normal.woff2";

/**
 * A DSA agent's signature, drawn from their profile name.
 *
 * Made the first time an Employer Certificate is generated for the agent and
 * then saved (POST /dsa-issuer/ensure-signature), so every later certificate
 * prints the same one. The admin's "Generate from profile name" on the
 * distributor / retailer profile uses this too, so its preview is exactly what
 * the certificate prints.
 *
 * The drawing follows the "Natural Signature Studio" page the client supplied:
 * each generate picks a handwriting face (random, or the one chosen), a
 * random size, weight and slant, a ballpoint ink that varies a little, a faint
 * ink underlay. So every click gives a different signature. The page's
 * finishing flourish under the name is NOT drawn — the client asked for the
 * name alone (16 Oct 2026).
 *
 * Unlike that page, the result is a TRANSPARENT PNG trimmed to the strokes
 * (no white paper or paper grain): the certificate prints it over its own
 * page and next to the seal.
 *
 * The fonts are bundled and served from the admin's own origin, and the draw
 * waits for them: a signature drawn in the fallback font would be saved, and
 * reused, for good.
 */

export type SignatureStyle =
  | "random"
  | "Dancing Script"
  | "Caveat"
  | "Allura"
  | "Great Vibes"
  | "Homemade Apple";
export type SignatureInk = "blue" | "black";

/** The dropdown on the profile card, in the page's order and wording. */
export const SIGNATURE_STYLE_OPTIONS: { value: SignatureStyle; label: string }[] = [
  { value: "random", label: "Random natural style" },
  { value: "Dancing Script", label: "Flowing cursive" },
  { value: "Caveat", label: "Casual handwriting" },
  { value: "Allura", label: "Elegant cursive" },
  { value: "Great Vibes", label: "Stylish cursive" },
  { value: "Homemade Apple", label: "Handwritten look" },
];
/** What a new signature is drawn with unless the user picks otherwise. */
export const DEFAULT_SIGNATURE_STYLE: SignatureStyle = "Homemade Apple";
export const DEFAULT_SIGNATURE_INK: SignatureInk = "black";

export const SIGNATURE_INK_OPTIONS: { value: SignatureInk; label: string }[] = [
  { value: "blue", label: "Blue ballpoint" },
  { value: "black", label: "Black pen" },
];

type FontName = Exclude<SignatureStyle, "random">;

/** Bundled faces. Where there is no 700 file the browser emboldens the 400. */
const FONT_FILES: Record<FontName, { weight: string; url: string }[]> = {
  "Dancing Script": [
    { weight: "400", url: dancingScript400 },
    { weight: "700", url: dancingScript700 },
  ],
  Caveat: [
    { weight: "400", url: caveat400 },
    { weight: "700", url: caveat700 },
  ],
  Allura: [{ weight: "400", url: allura400 }],
  "Great Vibes": [{ weight: "400", url: greatVibes400 }],
  "Homemade Apple": [{ weight: "400", url: homemadeApple400 }],
};
const FONTS = Object.keys(FONT_FILES) as FontName[];

/** Registered under our own family names, so nothing else on the page clashes. */
const familyOf = (font: FontName) => `SjbtSig ${font}`;

/** A random number between `min` and `max`. */
const rand = (min: number, max: number) => min + Math.random() * (max - min);

const fontReady: Partial<Record<FontName, Promise<void>>> = {};

const loadFont = (font: FontName) => {
  if (!fontReady[font]) {
    fontReady[font] = Promise.all(
      FONT_FILES[font].map(({ weight, url }) =>
        new FontFace(familyOf(font), `url(${url})`, { weight })
          .load()
          .then((loaded) => {
            (document.fonts as any).add(loaded);
          })
      )
    )
      .then(() => undefined)
      .catch((err) => {
        // Let the next call try again rather than caching the failure.
        delete fontReady[font];
        throw err;
      });
  }
  return fontReady[font]!;
};

/**
 * "MEETKUMAR SANTOSHBHAI GHODKE" → "Meetkumar Ghodke": first and last name,
 * title case, the way people sign.
 */
export const signatureNameFrom = (profileName?: string | null) => {
  const words = (profileName || "")
    .replace(/[^A-Za-z\s.]/g, " ")
    .split(/\s+/)
    .map((w) => w.replace(/^\.+|\.+$/g, ""))
    .filter(Boolean);
  const picked = words.length > 2 ? [words[0], words[words.length - 1]] : words;
  return picked
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(" ");
};

/** Crop a canvas to the bounding box of its painted pixels, plus `pad`. */
const trim = (canvas: HTMLCanvasElement, pad: number) => {
  const ctx = canvas.getContext("2d")!;
  const { width, height } = canvas;
  const { data } = ctx.getImageData(0, 0, width, height);
  let top = height;
  let left = width;
  let right = -1;
  let bottom = -1;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (data[(y * width + x) * 4 + 3] > 8) {
        if (x < left) left = x;
        if (x > right) right = x;
        if (y < top) top = y;
        if (y > bottom) bottom = y;
      }
    }
  }
  if (right < 0) return canvas;
  const out = document.createElement("canvas");
  out.width = right - left + 1 + pad * 2;
  out.height = bottom - top + 1 + pad * 2;
  out
    .getContext("2d")!
    .drawImage(canvas, left, top, right - left + 1, bottom - top + 1, pad, pad, right - left + 1, bottom - top + 1);
  return out;
};

/** Ballpoint ink that is never quite the same shade twice. */
const inkColor = (ink: SignatureInk) => {
  const c = (min: number, max: number) => Math.round(rand(min, max));
  return ink === "black"
    ? `rgb(${c(15, 35)},${c(15, 35)},${c(15, 35)})`
    : `rgb(${c(15, 35)},${c(40, 75)},${c(100, 160)})`;
};

/**
 * The signature as a transparent PNG data-URL, with the name it was drawn
 * from and the face used. Throws when there is no name to sign or the font
 * cannot load.
 */
export const generateSignature = async (
  profileName?: string | null,
  options: { style?: SignatureStyle; ink?: SignatureInk } = {}
): Promise<{ image: string; name: string; font: FontName }> => {
  const name = signatureNameFrom(profileName);
  if (!name) throw new Error("The profile has no name to sign with.");

  const style = options.style || DEFAULT_SIGNATURE_STYLE;
  const font: FontName =
    style === "random" ? FONTS[Math.floor(Math.random() * FONTS.length)] : style;
  await loadFont(font);

  const weight = Math.random() > 0.5 ? "400" : "700";
  const size = rand(95, 150);
  const slant = rand(-0.075, 0.075);
  const ink = inkColor(options.ink || DEFAULT_SIGNATURE_INK);
  const fontCss = `${weight} ${size}px "${familyOf(font)}"`;

  const measure = document.createElement("canvas").getContext("2d")!;
  measure.font = fontCss;
  const textWidth = Math.ceil(measure.measureText(name).width);

  // Room for the slant; trim() cuts the canvas back to the strokes.
  const canvas = document.createElement("canvas");
  canvas.width = Math.ceil(textWidth * 1.5 + size * 2);
  canvas.height = Math.ceil(size * 3.4);
  const ctx = canvas.getContext("2d")!;
  const cx = canvas.width / 2;
  const cy = size * 1.3;

  ctx.save();
  ctx.translate(cx, cy);
  ctx.rotate(slant);
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillStyle = ink;
  ctx.font = fontCss;
  // A faint, slightly offset underlay: a less mechanical ink edge.
  ctx.globalAlpha = 0.16;
  ctx.fillText(name, 0.7, 0.8);
  ctx.globalAlpha = 1;
  ctx.fillText(name, 0, 0);
  ctx.restore();

  return { image: trim(canvas, 6).toDataURL("image/png"), name, font };
};
